import { ThreadPrimitive, useAuiState } from '@assistant-ui/react';
import { Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowDown, IconMessages } from '@tabler/icons-react';
import { AnimatePresence, motion, stagger, useAnimate } from 'motion/react';
import { useDeferredValue, useEffect, useEffectEvent } from 'react';

import { AssistantMessage } from './AssistantMessage.tsx';
import { Composer } from './Composer.tsx';
import classes from './Thread.module.css';
import { UserMessage } from './UserMessage.tsx';
import { AtlasAiHero } from '#plugin-chat/components/hero/AtlasAiHero.tsx';
import { ThreadList } from '#plugin-chat/components/thread-list/ThreadList.tsx';
import { TooltipIconButton } from '#plugin-chat/components/tooltip-icon-button/TooltipIconButton.tsx';

/**
 * Main chat thread component orchestrating the conversation UI.
 * Uses assistant-ui primitives with custom message renderers and manual scroll control.
 *
 * Cross-turn dataset bindings are a pure projection of the active
 * message branch (resolved per tool-call part by `useBindingScope` at
 * the `<Tool>` render boundary), so the thread renders immediately —
 * there is no up-front hydration gate to wait on.
 */
export function Thread() {
  const threadId = useAuiState(({ threadListItem }) => threadListItem.id);
  const isEmpty = useAuiState(({ thread }) => thread.messages.length === 0 && !thread.isLoading);
  const [threadListOpen, { toggle: toggleThreadList }] = useDisclosure(false);

  return (
    <ThreadPrimitive.Root className={classes.chatThread}>
      <ThreadPrimitive.Viewport className={classes.viewport}>
        <AnimatePresence>{isEmpty && <AtlasAiHero key="hero" />}</AnimatePresence>

        <ViewportMessages threadId={threadId} />

        <ThreadPrimitive.ViewportFooter className={classes.viewportFooter}>
          <div className={classes.scrollToBottom}>
            <ThreadPrimitive.ScrollToBottom asChild>
              <TooltipIconButton position="top" radius="100%" size="lg" tooltip="Scroll to bottom" variant="outline">
                <IconArrowDown size={16} />
              </TooltipIconButton>
            </ThreadPrimitive.ScrollToBottom>
          </div>
          <Composer />
          <div className={classes.threadListToggle}>
            <Button
              color="dark.6"
              leftSection={<IconMessages size={16} />}
              onClick={toggleThreadList}
              radius="xl"
              size="xs"
              variant="light"
            >
              My Conversations
            </Button>
          </div>
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.Viewport>
      <AnimatePresence>
        {threadListOpen && (
          <motion.div
            animate={{ '--chat-thread-size-scale': 1, opacity: 1 }}
            className={classes.threadList}
            exit={{ '--chat-thread-size-scale': 0, opacity: 0 }}
            initial={{ '--chat-thread-size-scale': 0, opacity: 0 }}
            key="thread-list"
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <ThreadList />
          </motion.div>
        )}
      </AnimatePresence>
    </ThreadPrimitive.Root>
  );
}

function ViewportMessages({ threadId }: { threadId: string }) {
  const isLoading = useAuiState(({ thread }) => thread.isLoading);
  const deferredThreadId = useDeferredValue(threadId);
  const isDeferredLoading = (deferredThreadId && threadId !== deferredThreadId) || isLoading;

  const [scope, animate] = useAnimate();
  const animateEvent = useEffectEvent(animate);

  useEffect(() => {
    const elements = scope.current.querySelectorAll(`.${classes.contentWrapper}`);
    if (isDeferredLoading || !elements.length) {
      return;
    }
    animateEvent(
      elements,
      {
        transform: ['translateX(calc(var(--transform-multiplier) * -1 * var(--mantine-spacing-md)))', 'translateX(0)'],
        opacity: [0, 1],
      },
      { duration: 0.3, ease: 'easeOut', delay: stagger(0.15, { from: 'last' }) },
    );
  }, [isDeferredLoading, scope]);

  return (
    <div className={classes.viewportMessages} ref={scope}>
      {!isDeferredLoading && (
        <ThreadPrimitive.Messages>
          {({ message }) => {
            const Component = components[message.role];
            return <Component key={message.id} />;
          }}
        </ThreadPrimitive.Messages>
      )}
    </div>
  );
}

const components = {
  user: UserMessage,
  assistant: AssistantMessage,
  system: () => null,
} as const;
